import { useEffect, useState, useContext } from "react";
import axios from "axios";
import {
  Card,
  Typography,
  Spin,
  Button,
  Row,
  Col,
  Space,
  Select,
  Empty,
} from "antd";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { AuthContext } from "../../context/AuthContext";

const { Title, Text } = Typography;
const { Option } = Select;

export default function DepartmentWiseSalary() {
  const { authData, backUrl } = useContext(AuthContext) || { backUrl: "" };
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [organisations, setOrganisations] = useState([]);

  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  const [organisationId, setOrganisationId] = useState("ALL");
  const [month, setMonth] = useState(currentMonth);
  const [year, setYear] = useState(currentYear);

  const [currentBatch, setCurrentBatch] = useState(0);
  const batchSize = 10;

  // Fetch organisations
  useEffect(() => {
    const fetchOrganisations = async () => {
      try {
        const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
          headers: { Authorization: `Bearer ${authData?.access}` },
        });
        setOrganisations(res.data.results || res.data || []);
      } catch (err) {
        console.error("Error fetching organisations:", err);
      }
    };
    fetchOrganisations();
  }, [backUrl, authData?.access]);

  // Fetch salary data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = { month, year };
        if (organisationId !== "ALL") {
          params.organisation_id = organisationId;
        }

        const res = await axios.get(`${backUrl}/api/reports/department-salary/`, {
          params,
          headers: { Authorization: `Bearer ${authData?.access}` },
        });
        const salaryData = res.data || [];
        setData(salaryData);
        setCurrentBatch(0);
      } catch (err) {
        console.error("Error loading department salary:", err);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [organisationId, month, year, backUrl, authData?.access]);

  const startIndex = currentBatch * batchSize;
  const endIndex = startIndex + batchSize;
  const currentData = data.slice(startIndex, endIndex);

  const handleNext = () => {
    if (endIndex < data.length) setCurrentBatch(currentBatch + 1);
  };

  const handlePrev = () => {
    if (currentBatch > 0) setCurrentBatch(currentBatch - 1);
  };

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  return (
    <Card
      bordered={false}
      style={{
        borderRadius: 16,
        background: "#ffffff",
        boxShadow: "0 4px 16px rgba(15, 23, 42, 0.04)",
        border: "1px solid #e2e8f0",
        padding: 10,
      }}
    >
      <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={4} style={{ margin: 0, fontWeight: 800, color: "#0f172a" }}>
            Department-wise Salary Allocation
          </Title>
        </Col>
        <Col>
          <Space size="middle" wrap>
            <Select
              value={organisationId}
              onChange={(value) => setOrganisationId(value)}
              style={{ width: 220 }}
            >
              <Option value="ALL">All Organisations ({organisations.length})</Option>
              {organisations.map((org) => (
                <Option key={org.id} value={org.id}>
                  {org.name}
                </Option>
              ))}
            </Select>

            <Select
              value={month}
              onChange={(value) => setMonth(value)}
              style={{ width: 130 }}
            >
              {months.map((m, index) => (
                <Option key={index + 1} value={index + 1}>
                  {m}
                </Option>
              ))}
            </Select>

            <Select
              value={year}
              onChange={(value) => setYear(value)}
              style={{ width: 110 }}
            >
              {years.map((y) => (
                <Option key={y} value={y}>
                  {y}
                </Option>
              ))}
            </Select>
          </Space>
        </Col>
      </Row>

      {loading ? (
        <div style={{ textAlign: "center", padding: 60 }}>
          <Spin size="large" />
          <div style={{ marginTop: 12, color: "#64748b" }}>Loading Department Salary Allocation...</div>
        </div>
      ) : (
        <>
          {currentData.length === 0 ? (
            <div style={{ padding: "40px 0", textAlign: "center" }}>
              <Empty description="No salary data found for the selected period" />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={currentData} margin={{ top: 10, right: 30, left: 10, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="department_name" tick={{ fill: "#64748b", fontSize: 11, fontWeight: 600 }} />
                <YAxis
                  tick={{ fill: "#64748b", fontSize: 11 }}
                  tickFormatter={v => {
                    if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
                    if (v >= 1000) return `${(v / 1000).toFixed(0)}K`;
                    return v;
                  }}
                />
                <Tooltip
                  formatter={v => [`INR ${Number(v || 0).toLocaleString("en-IN")}`]}
                  contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }}
                />
                <Legend />
                <Bar dataKey="total_gross_salary" name="Gross Pay" fill="#0e497b" radius={[4, 4, 0, 0]} />
                <Bar dataKey="total_net_salary" name="Net Pay" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}

          {data.length > batchSize && (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 10 }}>
              <Button disabled={currentBatch === 0} onClick={handlePrev}>
                Previous
              </Button>
              <Button disabled={endIndex >= data.length} onClick={handleNext}>
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
